import  { useState, useEffect, Fragment } from "react";
import Layout from "../../components/Layout/Layout";
import {
  SearchIcon,
  UserGroupIcon,
  MailIcon,
  PhoneIcon,
  OfficeBuildingIcon,
  EyeIcon,
} from "@heroicons/react/outline";
import { Dialog } from "@headlessui/react";
import ActionDropdown from "../../components/ActionDropdown";
import fire from "./../../utils/Toastr";
import ThirdPartyServices from "../../services/ThirdParty";
import { HollowDotsSpinner } from "react-epic-spinners";
import Paginate from "../../utils/Paginate";
import { useSearchParams } from "react-router-dom";

export default function ThirdPartyEmployees() {
  const [searchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");

  // Pagination states
  const [pageIndex, setPageIndex] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Check for organization parameter in URL
  useEffect(() => {
    const orgFromUrl = searchParams.get("organization");
    if (orgFromUrl && orgFromUrl !== "undefined") {
      setSelectedOrgId(orgFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedOrgId && selectedOrgId !== "undefined") {
      fetchEmployees(1);
    }
  }, [selectedOrgId]);

  const fetchOrganizations = async () => {
    try {
      let allOrgs = [];
      let page = 1;
      let totalPages = 1;

      do {
        const response = await ThirdPartyServices.listOrganizations(page, 100);
        if (response.status && response.data?.result) {
          allOrgs = [...allOrgs, ...response.data.result];
          totalPages = response.data.totalPages || 1;
          page++;
        } else {
          break;
        }
      } while (page <= totalPages);

      setOrganizations(allOrgs);
      if (!selectedOrgId && allOrgs.length > 0) {
        setSelectedOrgId(allOrgs[0].organizationId);
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const fetchEmployees = async (page = 1) => {
    if (!selectedOrgId || selectedOrgId === "undefined") {
      setError("Please select an organization");
      setEmployees([]);
      return;
    }

    setLoading(true);
    try {
      const response = await ThirdPartyServices.listCustomers(
        page,
        10,
        selectedOrgId,
      );

      if (response.status && response.data?.result) {
        setEmployees(response.data.result);
        setPageIndex(response.data.pageIndex || page);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || 0);
        setHasNextPage(response.data.hasNextPage || false);
        setHasPreviousPage(response.data.hasPreviousPage || false);
      } else {
        setError(response.message || "Failed to fetch employees");
        setEmployees([]);
      }
    } catch (err) {
      setError("Failed to fetch employees");
      fire("error", "Failed to fetch employees");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim() || !selectedOrgId || selectedOrgId === "undefined") {
      fire("warning", "Please select an organization and enter search term");
      return;
    }

    setLoading(true);
    try {
      const response = await ThirdPartyServices.getEmployee(
        selectedOrgId,
        searchTerm,
      );

      if (response.status && response.data) {
        setEmployees([response.data]);
        setTotalCount(1);
        setTotalPages(1);
        setPageIndex(1);
      } else {
        fire("info", "No employee found");
        setEmployees([]);
        setTotalCount(0);
      }
    } catch (err) {
      fire("error", "Search failed");
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (employee) => {
    try {
      const response = await ThirdPartyServices.sendEmployeeNotification({
        employeeId: employee.id,
        organizationId: employee.organizationId,
        email: employee.emailAddress,
        name: `${employee.firstName} ${employee.surname}`,
      });

      if (response.status) {
        fire("success", "Notification sent successfully");
      } else {
        fire("error", response.message || "Failed to send notification");
      }
    } catch (err) {
      fire("error", "Failed to send notification");
      console.error("Notification error:", err);
    }
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    fetchEmployees(newPage);
  };

  const viewEmployeeDetails = (employee) => {
    setSelectedEmployee(employee);
    setShowDetailsModal(true);
  };

  return (
    <Layout
      title="Third-Party Employees"
      current="thirdparty"
      pageTag="thirdparty"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="sm:flex sm:items-center sm:justify-between mb-8">
            <div>
              <p className="mt-2 text-sm text-gray-700">
                Manage employees from third-party organizations
              </p>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Organization Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization *
                </label>
                <select
                  value={selectedOrgId}
                  onChange={(e) => {
                    const orgId = e.target.value;
                    setSelectedOrgId(orgId);
                    // Reset search when organization changes
                    setSearchTerm("");
                    setPageIndex(1);
                  }}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.organizationId} value={org.organizationId}>
                      {org.companyName}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Organization must be selected to view employees
                </p>
              </div>

              {/* Search Input - Only enable if organization is selected */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Employees
                </label>
                <form onSubmit={handleSearch} className="flex space-x-2">
                  <div className="flex-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SearchIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`pl-10 block w-full border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                        !selectedOrgId ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder={
                        selectedOrgId
                          ? "Search by name or ID..."
                          : "Select organization first"
                      }
                      disabled={!selectedOrgId}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!selectedOrgId}
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                      selectedOrgId
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-gray-400 cursor-not-allowed"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                  >
                    Search
                  </button>
                </form>
              </div>

              {/* Stats */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex items-center">
                  <UserGroupIcon className="h-8 w-8 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Total Employees</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {totalCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <HollowDotsSpinner color="#4A90E2" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-700">{error}</p>
            </div>
          ) : !selectedOrgId || selectedOrgId === "undefined" ? (
            <div className="text-center py-12">
              <OfficeBuildingIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select an Organization
              </h3>
              <p className="text-gray-500">
                Please select an organization to view employees.
              </p>
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No employees found
              </h3>
              <p className="text-gray-500">
                There are no employees for the selected organization.
              </p>
            </div>
          ) : (
            <>
              {/* Employees Table */}
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Organization
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {employees.map((employee, i) => (
                        <tr key={employee.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="h-10 w-10 flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                                  <span className="text-red-600 font-semibold">
                                    {employee.firstName?.charAt(0)}
                                    {employee.surname?.charAt(0)}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {employee.firstName} {employee.surname}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: {employee.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center text-sm text-gray-500">
                                <MailIcon className="h-4 w-4 mr-2" />
                                {employee.emailAddress}
                              </div>
                              <div className="flex items-center text-sm text-gray-500">
                                <PhoneIcon className="h-4 w-4 mr-2" />
                                {employee.phoneNumber}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {organizations.find(
                                (o) =>
                                  o.organizationId === employee.organizationId,
                              )?.companyName || "N/A"}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {employee.organizationId}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <ActionDropdown
                              items={[
                                { label: "View Details", onClick: () => viewEmployeeDetails(employee), icon: EyeIcon },
                                { label: "Send Notification", onClick: () => handleSendNotification(employee), icon: MailIcon },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6">
                  <Paginate
                    nextPage={hasNextPage}
                    pageIndex={pageIndex}
                    totalCount={totalCount}
                    prevPage={hasPreviousPage}
                    totalPages={totalPages}
                    setPageIndex={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Employee Details Modal */}
      {/* Employee Details Modal */}
      <Dialog
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-3xl w-full rounded-lg bg-white">
            {selectedEmployee && (
              <>
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-gray-900">
                      Employee Details
                    </Dialog.Title>
                    <button
                      onClick={() => setShowDetailsModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <svg
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Personal Information */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-4">
                        Personal Information
                      </h3>
                      <dl className="space-y-4">
                        <div>
                          <dt className="text-xs text-gray-500">Full Name</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedEmployee.firstName}{" "}
                            {selectedEmployee.surname}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Title</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedEmployee.title}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Gender</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedEmployee.gender}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">
                            Date of Birth
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {new Date(
                              selectedEmployee.dateOfBirth,
                            ).toLocaleDateString()}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">
                            Marital Status
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedEmployee.maritalStatus}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Contact & Identification */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-4">
                        Contact & Identification
                      </h3>
                      <dl className="space-y-4">
                        <div>
                          <dt className="text-xs text-gray-500">
                            Email Address
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedEmployee.emailAddress}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">
                            Phone Number
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedEmployee.phoneNumber}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Address</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedEmployee.residentialAddress}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">State</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedEmployee.state}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">BVN</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedEmployee.bvn}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">NIN</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {selectedEmployee.nationalIdentificationNumber}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>

                  {/* Account Information */}
                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-500 mb-4">
                      Account Information
                    </h3>
                    <dl className="grid grid-cols-2 gap-4">
                      <div>
                        <dt className="text-xs text-gray-500">Username</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {selectedEmployee.userName}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">
                          Organization ID
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {selectedEmployee.organizationId}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Date Created</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {new Date(
                            selectedEmployee.dateCreated,
                          ).toLocaleDateString()}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => handleSendNotification(selectedEmployee)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <MailIcon className="h-4 w-4 mr-2" />
                    Send Notification
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDetailsModal(false)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>
    </Layout>
  );
}
